import { Row } from '@tanstack/react-table'
import { Pencil, Trash2, KeyRound, ShieldCheck, ShieldOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { RowActionBar, type RowAction } from '@/components/ui/row-action-bar'
import { useUsers } from '../context/users-context'
import { User } from '../data/schema'
import { useAuth } from '@/stores/authStore'
import { UserPermission } from '@/constants/permissions'

interface DataTableRowActionsProps {
  row: Row<User>
}

export function DataTableRowActions({ row }: DataTableRowActionsProps) {
  const { t } = useTranslation()
  const { setOpen, setCurrentRow } = useUsers()
  const { can } = useAuth()

  const canChange = can(UserPermission.CHANGE_USER)
  const canDelete = can(UserPermission.DELETE_USER)
  const isAdmin = row.original.is_admin

  const actions: RowAction[] = [
    {
      key: 'reset_pass',
      icon: <KeyRound className="h-4 w-4" />,
      label: t('reset_pass'),
      hidden: !canChange,
      onClick: () => {
        setCurrentRow(row.original)
        setOpen('reset_pass')
      },
    },
    {
      key: 'edit',
      icon: <Pencil className="h-4 w-4" />,
      label: t('edit'),
      hidden: !canChange,
      onClick: () => {
        setCurrentRow(row.original)
        setOpen('edit')
      },
    },
    {
      key: 'make_admin',
      icon: <ShieldCheck className="h-4 w-4" />,
      label: t('make_admin'),
      hidden: !canChange || isAdmin,
      onClick: () => {
        setCurrentRow(row.original)
        setOpen('make_admin')
      },
    },
    {
      key: 'revoke_admin',
      icon: <ShieldOff className="h-4 w-4" />,
      label: t('revoke_admin'),
      hidden: !canChange || !isAdmin,
      onClick: () => {
        setCurrentRow(row.original)
        setOpen('revoke_admin')
      },
    },
    {
      key: 'delete',
      icon: <Trash2 className="h-4 w-4" />,
      label: t('delete'),
      destructive: true,
      hidden: !canDelete,
      onClick: () => {
        setCurrentRow(row.original)
        setOpen('delete')
      },
    },
  ]

  return <RowActionBar actions={actions} />
}
